<template>
  <div>
    <div class="flex items-center mb-4" v-if="event">
      <span
        class="text-grey-darker mr-2 text-2xl cursor-pointer"
        @click="$router.push({ name: 'dashboard' })"
        >Veranstaltungen</span
      >
      <font-awesome-icon icon="chevron-right"></font-awesome-icon>
      <span class="text-3xl ml-2" v-text="event.title"></span>
    </div>
    <div class="mb-8">
      <div class="flex">
        <span class="item-center mr-2 text-gray-900">
          <font-awesome-icon icon="calendar"></font-awesome-icon>
        </span>
        <span class="item-center">{{ event.start_date | date }} Uhr</span>
      </div>

      <div>
        <span class="item-center mr-2 text-gray-900">
          <font-awesome-icon icon="map-marker-alt"></font-awesome-icon>
        </span>
        <span>{{ event.venue.name }} - {{ event.venue.city }}</span>
      </div>
    </div>

    <div v-if="tickets.categories.length < 1" class="text-red-500 bg-red-200 p-4 rounded">
      Für diese Veranstaltung stehen momentan keine Tickettypen zur Verfügung. Das kann folgende
      Gründe haben:

      <ul class="list-disc list-inside mt-2">
        <li>
          Der Veranstalter hat den Verkauf nicht für das localTicketing Vorverkaufsstellen Netzwerk
          freigeschaltet.
        </li>
        <li>Der Verkauf wurde beendet</li>
        <li>Der Verkauf hat noch nicht begonnen</li>
      </ul>
    </div>

    <div v-else>
      <VueBestAvailableSelector
        :event="tickets"
        @updated="updated"
        v-if="!event.is_reserved_seating"
      ></VueBestAvailableSelector>

      <div v-else>
        <VueSeatSelector
          :pricing="tickets"
          :event="event"
          @updated="updated"
          @selectionValidity="selectionValidity"
        />
      </div>
    </div>

    <div
      class="sticky bottom-0 shadow rounded bg-white border px-6 pt-6 mt-6"
      v-if="itemCount > 0 && !isCompleting"
    >
      <div :class="loading && 'opacity-50'">
        <div class="text-2xl">Gesamtbetrag: {{ (itemTotal / 100) | price }} Euro</div>

        <div>{{ itemCount }}x {{ itemCount > 1 ? 'Tickets' : 'Ticket' }}</div>

        <div class="my-4 mb-4 p-3 bg-red-200 rounded text-red-600 flex" v-if="hasValidationErrors">
          <span class="item-center">
            <font-awesome-icon icon="exclamation-circle"></font-awesome-icon>
          </span>
          <span class="ml-2 items-center">{{
            validationErrors[Object.keys(validationErrors)[0]]
          }}</span>
        </div>

        <div class="mb-4 w-1/4" v-if="!hasValidationErrors">
          <button
            @click="create"
            class="btn-small w-full py-3 btn-primary mt-10 mb-2"
            :disabled="loading"
          >
            {{ itemCount }} {{ itemCount > 1 ? 'Tickets' : 'Ticket' }} reservieren
          </button>
        </div>
      </div>

      <div class="absolute w-full top-0 right-0 h-full" v-if="loading"></div>
      <div class="spinner z-20" v-if="loading"></div>
    </div>

    <div
      v-if="isCompleting"
      class="fixed bottom-0 inset-x-0 px-4 pb-6 sm:inset-0 sm:p-0 sm:flex sm:items-center sm:justify-center z-20 overflow-auto"
    >
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div
        class="relative bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full sm:p-6"
      >
        <form @submit.prevent="place">
          <div>
            <div
              class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100"
            >
              <font-awesome-icon
                class="text-green-500 text-xl"
                :icon="['far', 'hand-holding-usd']"
              />
            </div>
            <div class="mt-3 text-center sm:mt-5">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                {{ (checkoutTotal / 100) | price }} Euro Zahlung ausführen
              </h3>
            </div>

            <div v-if="!waitingForTerminal" class="mt-4 rounded-md bg-yellow-100 p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg
                    class="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-yellow-800">Abfrage von Kundendaten</h3>
                  <div class="mt-2 text-sm text-yellow-700">
                    <p>
                      Die Abfrage von Kundendaten ist freiwillig, führt aber zu einer Vereinfachung
                      der Kommunikation bei Absagen oder Verlegungen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex mt-5" v-if="!waitingForTerminal">
              <div class="w-full mr-1">
                <label for="first_name" class="block text-sm font-medium leading-5 text-gray-700"
                  >Vorname</label
                >
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input
                    id="first_name"
                    v-model="details.first_name"
                    :disabled="loading || waitingForTerminal"
                    class="form-input block w-full sm:text-sm sm:leading-5"
                  />
                </div>
              </div>
              <div class="w-full ml-1">
                <label for="last_name" class="block text-sm font-medium leading-5 text-gray-700"
                  >Nachname</label
                >
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input
                    id="last_name"
                    v-model="details.last_name"
                    :disabled="loading || waitingForTerminal"
                    class="form-input block w-full sm:text-sm sm:leading-5"
                  />
                </div>
              </div>
            </div>

            <div class="flex mt-3" v-if="!waitingForTerminal">
              <div class="w-full mr-1">
                <label for="email" class="block text-sm font-medium leading-5 text-gray-700"
                  >E-Mail Adresse</label
                >
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input
                    id="email"
                    v-model="details.email"
                    type="email"
                    :disabled="loading || waitingForTerminal"
                    class="form-input block w-full sm:text-sm sm:leading-5"
                    data-1p-ignore
                  />
                </div>
              </div>
            </div>

            <div class="mt-5 text-left" v-if="terminalEnabled && !waitingForTerminal">
              <div class="text-sm font-medium text-gray-700 mb-2">Zahlungsart</div>
              <label class="flex items-center mb-2">
                <input
                  type="radio"
                  class="mr-2"
                  value="cash"
                  v-model="paymentType"
                  :disabled="loading"
                />
                Bar
              </label>
              <label class="flex items-center">
                <input
                  type="radio"
                  class="mr-2"
                  value="terminal"
                  v-model="paymentType"
                  :disabled="loading"
                />
                Karte (Terminal)
              </label>
            </div>

            <div
              v-if="waitingForTerminal"
              class="mt-6 rounded-md bg-blue-50 border border-blue-200 p-6 text-center"
            >
              <div class="relative h-16 mb-2">
                <div class="spinner"></div>
              </div>
              <p class="text-base font-medium text-blue-900">
                {{
                  cancellingTerminal
                    ? 'Zahlung wird abgebrochen…'
                    : 'Bitte Karte am Terminal vorhalten'
                }}
              </p>
              <p class="mt-2 text-sm text-blue-800">
                {{ checkoutCount }} Ticket{{ checkoutCount === 1 ? '' : 's' }} ·
                {{ (checkoutTotal / 100) | price }}
              </p>
              <button
                type="button"
                @click="cancelTerminal"
                :disabled="cancellingTerminal"
                class="mt-5 inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none disabled:opacity-50"
              >
                {{ cancellingTerminal ? 'Wird abgebrochen...' : 'Zahlung abbrechen' }}
              </button>
            </div>
          </div>
          <div
            v-if="!waitingForTerminal"
            class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
          >
            <span class="flex w-full rounded-md shadow-sm sm:col-start-2">
              <button
                type="submit"
                :disabled="loading"
                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-500 leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50"
              >
                {{ loading ? 'Wird verarbeitet...' : checkoutCount + ' Ticket(s) kaufen' }}
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:col-start-1">
              <button
                @click="isCompleting = false"
                type="button"
                :disabled="loading"
                class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50"
              >
                Abbrechen
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import VueBestAvailableSelector from '@/components/VueBestAvailableSelector';
import VueSeatSelector from '@/components/SeatsSelector';
import eventsClient from '@/api/commerce/events';
import ordersClient from '@/api/commerce/orders';
import dayjs from 'dayjs';

import _sumBy from 'lodash/sumBy';
import _isEmpty from 'lodash/isEmpty';

export default {
  props: ['event'],

  components: {
    VueBestAvailableSelector,
    VueSeatSelector,
  },

  filters: {
    date(value) {
      return dayjs(value).format('dddd, DD MMMM YYYY HH:mm');
    },
    price(value) {
      var formatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      });

      return formatter.format(value);
    },
    timeLeftMinutes(value) {
      return Math.floor((value % (60 * 60)) / 60);
    },
    timeLeftSeconds(value) {
      return ('0' + Math.floor(value % 60).toString()).slice(-2);
    },
  },

  data() {
    return {
      tickets: {},
      items: [],
      loading: false,
      validationErrors: {},
      isCompleting: false,
      order: null,
      details: {
        email: '',
        first_name: '',
        last_name: '',
      },
      paymentType: 'cash',
      waitingForTerminal: false,
      cancellingTerminal: false,
      userCancelledTerminal: false,
      terminalPollActive: false,
      activePaymentId: null,
      checkoutTotal: 0,
      checkoutCount: 0,
    };
  },

  computed: {
    terminalEnabled() {
      return !!this.$store.state.account.currentAccount.terminal_enabled;
    },

    itemCount() {
      return this.items.length;
    },

    itemTotal() {
      return _sumBy(this.items, 'price');
    },

    hasValidationErrors() {
      return !_isEmpty(this.validationErrors);
    },
  },

  mounted() {
    eventsClient.show(this.$route.params.id).then(({ data }) => (this.tickets = data.data));
  },

  methods: {
    updated(items) {
      this.items = items;

      // Remove to-many-tickets validation error
      this.$delete(this.validationErrors, 'to-many-tickets');
      this.$delete(this.validationErrors, 'to-few-tickets');
      this.$delete(this.validationErrors, 'wrong-increment');

      // Test if to many tickets
      if (this.itemCount > this.tickets.max_amount_per_order) {
        this.$set(
          this.validationErrors,
          'to-many-tickets',
          `Bitte weniger als ${this.tickets.max_amount_per_order} Tickets wählen.`
        );
      }
      // Test if to few tickets
      if (this.itemCount < this.tickets.min_amount_per_order) {
        this.$set(
          this.validationErrors,
          'to-few-tickets',
          `Bitte mindestens ${this.tickets.min_amount_per_order} Tickets wählen.`
        );
      }
      if (this.itemCount % this.tickets.increment_per_order !== 0) {
        this.$set(
          this.validationErrors,
          'wrong-increment',
          `Bitte in ${this.tickets.increment_per_order}er Bündeln auswählen.`
        );
      }
    },
    selectionValidity(validity) {
      validity
        ? this.$delete(this.validationErrors, 'no-orphan-seats')
        : this.$set(
            this.validationErrors,
            'no-orphan-seats',
            'Bitte vermeiden Sie leere Einzelplätze.'
          );
    },
    create() {
      this.checkoutTotal = this.itemTotal;
      this.checkoutCount = this.itemCount;
      this.loading = true;

      ordersClient
        .create({
          event_id: this.event.id,
          channel: 'agency',
          tickets: this.items,
        })
        .then(({ data }) => {
          this.order = data.data;
          this.isCompleting = true;
        })
        .catch(() => {
          // set to-many-tickets validation error
          this.$set(this.validationErrors, 'to-many-tickets', 'Bitte weniger Tickets wählen.');
        })
        .then(() => (this.loading = false));
    },
    finishSuccess() {
        this.$router.push({ name: 'order', params: { id: this.order.id } });
    },
    async pollPayment(paymentId) {
      const deadline = Date.now() + 120000;
      this.terminalPollActive = true;

      try {
        while (Date.now() < deadline && this.terminalPollActive) {
          const { data } = await ordersClient.showPayment(paymentId, this.order.signature);
          const payment = data.data;

          if (payment.status === 'completed' || payment.status === 'cancelled') {
            return payment;
          }

          await new Promise((resolve) => setTimeout(resolve, this.cancellingTerminal ? 400 : 1500));
        }

        throw new Error('timeout');
      } finally {
        this.terminalPollActive = false;
      }
    },
    async cancelTerminal() {
      if (!this.activePaymentId || this.cancellingTerminal) {
        return;
      }

      this.userCancelledTerminal = true;
      this.cancellingTerminal = true;

      try {
        await ordersClient.cancelPayment(this.activePaymentId, this.order.signature);
      } catch (ignored) {}
    },
    async place() {
      this.loading = true;
      this.userCancelledTerminal = false;
      this.activePaymentId = null;

      try {
        await ordersClient.update(
          this.order.id,
          {
            first_name: this.details.first_name,
            last_name: this.details.last_name,
            email: this.details.email.length > 0 ? this.details.email : undefined,
          },
          this.order.signature
        );

        const { data } = await ordersClient.place(
          this.order.id,
          {
            payment_type: this.terminalEnabled ? this.paymentType : 'cash',
          },
          this.order.signature
        );
        const payment = data.data;
        this.activePaymentId = payment.id;

        if (payment.status === 'completed') {
          this.finishSuccess();
          return;
        }

        if (this.paymentType === 'terminal') {
          this.loading = false;
          this.waitingForTerminal = true;
          const finalPayment = await this.pollPayment(payment.id);

          if (finalPayment.status === 'completed') {
            this.finishSuccess();
            return;
          }

          if (this.userCancelledTerminal && finalPayment.status === 'cancelled') {
            return;
          }
        }

        throw new Error('payment not completed');
      } catch (e) {
        if (this.activePaymentId && this.paymentType === 'terminal') {
          try {
            const { data } = await ordersClient.cancelPayment(
              this.activePaymentId,
              this.order.signature
            );

            if (data.data.status === 'completed') {
              this.finishSuccess();
              return;
            }

            if (this.userCancelledTerminal && data.data.status === 'cancelled') {
              return;
            }
          } catch (ignored) {}
        }

        this.$toasted.show('Zahlung war nicht erfolgreich. Bitte erneut probieren.', {
          type: 'error',
          theme: 'lt',
          duration: 3000,
          position: 'bottom-left',
        });
      } finally {
        this.loading = false;
        this.waitingForTerminal = false;
        this.cancellingTerminal = false;
        this.terminalPollActive = false;
      }
    },
    setCompanions(key, value) {
      let companions = this.details.companions;
      let companionsArray;

      if (companions.length > 0) {
        companionsArray = companions.split(';');
      } else {
        companionsArray = new Array(this.order.tickets.length);
      }

      companionsArray[key] = value;

      this.details.companions = companionsArray.join(';');
    },

    getCompanions(key) {
      let companions = this.details.companions;

      if (companions.length > 0) {
        return companions.split(';')[key];
      }

      return '';
    },
  },
};
</script>
